import { useState } from 'react';
import * as React from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import CustomIcon from '@components/shared/CustomIcon';
import { maxBy } from 'lodash';
import cs from 'classnames';
import ExclusiveLabel from '@components/shared/ExclusiveLabel';
import ChairCircle from '@components/layouts/Truva/Building/AvailableSpaces/chair-circle.svg';
import ChairStrikethroughCircle from '@components/layouts/Truva/Building/AvailableSpaces/chair-strikethrough-circle.svg';
import { BuildingListing, TourbookSummary, StoreState } from '@root/types';
import routes from '@root/routes';
import actions from '@store/actions/buildingPage';
import { useTranslation } from 'react-i18next';
import useToggle from '@shared/useToggle';
import TourbookIconButton from '@components/shared/TourbookIconButton';
import { TourbookOverlay } from '@components/shared';
import { tourbookCreateOnly } from '@store/selectors';
import FlexibleSizeArea from '@components/shared/FlexibleSizeArea';
import css from './Mobile.module.less';
import FloorPlanModal from './FloorPlanModal';

const mapState = (state: StoreState) => ({
  isTourbookCreateOnly: tourbookCreateOnly(state),
});

const mapDispatch = {};

const connector = connect(mapState, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

type Props = {
  listing: BuildingListing;
  buildingSlug: string;
  listingClicked: () => void;
  isInTourbook: boolean;
  tourbooks: TourbookSummary[];
  isUserLoggedIn: boolean;
};

const AvailableSpaceCard = ({
  listing,
  buildingSlug,
  listingClicked,
  isInTourbook,
  tourbooks,
  isTourbookCreateOnly,
  isUserLoggedIn,
}: Props & ReduxProps) => {
  const { t } = useTranslation('building');
  const {
    value: tourbookOverlayIsOpen,
    setTrue: openTourbookOverlay,
    setFalse: closeTourbookOverlay,
  } = useToggle();
  const [showFloorPlan, setShowFloorPlan] = useState(false);
  const dispatch = useDispatch();

  const showPricingInfo = listing.baseRents && listing.baseRents.length > 0 && listing.area;
  const baseRent = showPricingInfo ? maxBy(listing.baseRents, 'month') : null;

  return (
    <div className={css.mobileCardWrapper}>
      <div
        className={css.mobileCard}
        onClick={() => {
          listingClicked();
          window.open(routes.listing(listing.id, buildingSlug), '_blank');
        }}
        data-testid="listing-card"
        role="link"
      >
        <div className={css.cardHeader}>
          {listing.availableToday
            ? t('availableSpaces.mobile.immediateAvailability')
            : t('availableSpaces.mobile.dateAvailable', {
                date: listing.availabilityDate,
              })}
        </div>
        <div className={css.cardBody}>
          <div>
            <div
              className={cs(css.cardImg, listing.floorPlan ? css.partialHeight : css.fullHeight)}
            >
              {listing.isCurrentlyExclusive && <ExclusiveLabel size="small" />}
              <div className={css.actionButtonWrapper}>
                <TourbookIconButton
                  isInTourbook={isInTourbook}
                  setTourbookDialogIsOpen={openTourbookOverlay}
                  className={css.tourbookIcon}
                />
              </div>
              <img src={listing.previewPhoto.smallPath} alt={listing.previewPhoto.description} />
            </div>
            {listing.floorPlan ? (
              <button
                type="button"
                className={css.floorPlanButton}
                onClick={e => {
                  e.stopPropagation();
                  dispatch(actions.buildingSpacesfloorplanClicked());
                  setShowFloorPlan(true);
                }}
              >
                <CustomIcon className={css.floorPlanIcon} type="floor-plan" />
                {t('availableSpaces.floorPlanTitle')}
              </button>
            ) : null}
          </div>
          <div className={css.cardInfo}>
            <div className={css.bgText}>
              <b>{listing.suiteName}</b>
            </div>
            <div className={css.bgText}>{listing.floorName}</div>
            <div className={cs(css.smText, css.areaInfo)}>
              {t('units:area', { area: listing.area })}
              <FlexibleSizeArea minMaxArea={listing.minMaxArea} />
            </div>

            {isUserLoggedIn && (
              <div className={cs(css.bgText, css.leaseType)}>
                {t(`listing:leaseTypes.${listing.leaseType}`)}
              </div>
            )}

            <div className={css.priceFurnishedWrapper}>
              <div data-testid="priceInformation" className={css.smText}>
                {showPricingInfo
                  ? t('units:pricePerAreaPerPeriod', {
                      pricePerAreaPerPeriod: baseRent,
                    })
                  : null}
              </div>
              <div className={css.mobileFurnishedIcon}>
                {listing.furnished ? (
                  <ChairCircle width="100%" height="100%" data-testid="furnishedIcon" />
                ) : (
                  <ChairStrikethroughCircle
                    width="100%"
                    height="100%"
                    data-testid="unfurnishedIcon"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {showFloorPlan ? (
          <FloorPlanModal
            floorPlanUrl={listing.floorPlan as string}
            onClose={(e: React.MouseEvent) => {
              e.stopPropagation();
              setShowFloorPlan(false);
            }}
          />
        ) : null}
      </div>
      {tourbookOverlayIsOpen && (
        <TourbookOverlay
          isOpen={tourbookOverlayIsOpen}
          close={closeTourbookOverlay}
          listing={listing}
          createOnly={isTourbookCreateOnly}
          tourbooks={tourbooks}
          className={css.tourbookOverlay}
        />
      )}
    </div>
  );
};

export default connector(AvailableSpaceCard);
